import { type SQLQueryBindings } from "bun:sqlite";

import { type Primitive } from "../types";

class SqlString {
	constructor(
		public readonly statement: string,
		public readonly params: SQLQueryBindings[] = []
	) {}
}

function sql(strings: TemplateStringsArray, ...values: unknown[]): SqlString {
	const params: SQLQueryBindings[] = [];
	const result = strings.reduce((acc, str, i) => {
		if (i === strings.length - 1) {
			return acc + str;
		}

		const value = values[i];
		// if (value instanceof QueryBuilder) {
		// 	const q = value.toQuery();
		// 	params.push(...(q.params ?? []));
		// 	return acc + str + `(${q.statement})`;
		// }
		if (value instanceof SqlString) {
			params.push(...value.params);
			return acc + str + value.statement;
		}
		params.push(value as SQLQueryBindings);
		return acc + str + "?";
	}, "");

	return new SqlString(result, params);
}

// Helper for escaping identifiers
sql.id = (identifier: string) => sql`"${sql.escape(identifier)}"`;

// Helper for literal values that should not be parameterized
sql.lit = (value: Primitive): SqlString => {
	if (value === null) {
		return new SqlString("NULL");
	}

	if (typeof value === "string") {
		return sql.escape(value);
	}

	return new SqlString(String(value));
};

// Helper for references that should not be escaped
sql.ref = (reference: string) => new SqlString(reference);

// Helper for joining multiple SQL expressions
sql.join = (exprs: SqlString[], separator = ", ", indentLevel = 0) => {
	if (exprs.length === 0) {
		return new SqlString("");
	}

	const indent = sql.indent(indentLevel).statement;

	const params: SQLQueryBindings[] = [];
	const statement = exprs
		.map((expr) => {
			params.push(...expr.params);
			return expr.statement;
		})
		.join(separator + indent);

	return new SqlString(statement, params);
};

sql.escape = (fragment: string) => {
	// NOTE: Some day when we support things like MySQL's `NO_BACKSLASH_ESCAPES`[1], we'll need to
	// do something like replicate the behavior of PHP's `mysql_real_escape_string`, which escapes
	// \x00, \n, \r, \, ', " and \x1a. This is implemented thus:
	//
	//   return new SqlString(fragment.replace(/[\x00-\x1a\x1c-\x1f\x7f]/g, (char) => `\\${char}`));
	//
	// Until then we can just replace `"` with `""` because that's what SQLite allows by default for
	// escaping that character.
	//
	// [1]: https://dev.mysql.com/doc/refman/5.7/en/sql-mode.html#sqlmode_no_backslash_escapes
	return new SqlString(fragment.replace(/"/g, '""'));
};

// In standard SQL, quotes are escaped by adding two `"` characters.
sql.quote = (fragment: string) => sql`"${sql.escape(fragment)}"`;

sql.indent = (indentLevel: number) => new SqlString("    ".repeat(indentLevel));

export { sql, SqlString };
